<template>
  <div class="d-flex flex-column align-items-center">
    <content-header title="Settings" :subTitle="subTitle" class="w-100 mxw-md px-10"/>
    <content-body-card class="w-100 mxw-md">
      <!-- Password Form -->
      <el-form ref="passwordForm" :model="password" :rules="passwordRules" label-position="top">
        <h3 class="f-light">Change Password</h3>
        <el-row :gutter="20">
          <el-col :span="24" :md="12" class="mt-10">
            <el-input-title required>Current Password</el-input-title>
            <el-form-item prop="current">
              <el-input
                class="mt-05"
                type="password"
                v-model="password.current"
                placeholder="Type here..."
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :lg="12" class="mt-10">
            <el-input-title required>New Password</el-input-title>
            <el-form-item prop="update">
              <el-input
                class="mt-05"
                type="password"
                v-model="password.update"
                placeholder="Type here..."
                @input="updatePasswordRequirements"
              />
            </el-form-item>
            <!-- Password Requirements -->
            <div v-if="password.update" class="password-requirements mt-10">
              <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.minLength }">
                <i :class="passwordRequirements.minLength ? 'el-icon-check' : 'el-icon-close'"></i>
                <span>Minimum 8 characters</span>
              </div>
              <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.hasUpperCase }">
                <i :class="passwordRequirements.hasUpperCase ? 'el-icon-check' : 'el-icon-close'"></i>
                <span>At least one uppercase letter</span>
              </div>
              <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.hasLowerCase }">
                <i :class="passwordRequirements.hasLowerCase ? 'el-icon-check' : 'el-icon-close'"></i>
                <span>At least one lowercase letter</span>
              </div>
              <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.hasSpecialChar }">
                <i :class="passwordRequirements.hasSpecialChar ? 'el-icon-check' : 'el-icon-close'"></i>
                <span>At least one special character</span>
              </div>
              <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.noSpaces }">
                <i :class="passwordRequirements.noSpaces ? 'el-icon-check' : 'el-icon-close'"></i>
                <span>No spaces allowed</span>
              </div>
            </div>
          </el-col>
          <el-col :lg="12" class="mt-10">
            <el-input-title required>Confirm New Password</el-input-title>
            <el-form-item prop="confirm">
              <el-input
                class="mt-05"
                type="password"
                v-model="password.confirm"
                placeholder="Type here..."
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row class="mt-10">
          <el-divider></el-divider>
        </el-row>
      </el-form>

      <!-- Profile Information Form -->
      <el-form ref="profileForm" :model="profile" :rules="profileRules" label-position="top">
        <h3 class="f-light mt-20">Profile Information</h3>
        <p class="text-muted">Update your personal information</p>

        <el-row :gutter="20" class="mt-10">
          <el-col :span="24" :md="12">
            <el-input-title required>Gender</el-input-title>
            <el-form-item prop="gender">
              <el-select
                class="mt-05 w-100"
                v-model="profile.gender"
                placeholder="Select gender"
              >
                <el-option
                  v-for="item in genderOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider class="mt-20"></el-divider>
      </el-form>

      <!-- 2FA Form -->
      <el-form ref="twoFactorForm" :model="twoFactorAuth" label-position="top">
        <h3 class="f-light mt-20">Two-Factor Authentication (2FA)</h3>
        <p class="text-muted">Add an extra layer of security to your account</p>

        <el-row :gutter="20" class="mt-10">
          <el-col :span="24">
            <el-switch
              v-model="twoFactorAuth.enabled"
              active-text="Enable 2FA"
              inactive-text="Disable 2FA"
              @change="handle2FAToggle"
            ></el-switch>
          </el-col>
        </el-row>

        <el-row :gutter="20" v-if="twoFactorAuth.enabled" class="mt-15">
          <el-col :span="24">
            <el-radio-group v-model="twoFactorAuth.method">
              <el-radio label="mobile">Mobile</el-radio>
              <el-radio label="email">Email</el-radio>
            </el-radio-group>
          </el-col>
        </el-row>

        <el-row :gutter="20" v-if="twoFactorAuth.enabled" class="mt-10">
          <el-col :span="24" :md="12" v-if="twoFactorAuth.method === 'mobile'">
            <el-input-title required>Mobile Number</el-input-title>
            <el-form-item prop="mobileNumber" :rules="twoFactorAuthRules.mobileNumber">
              <el-input
                class="mt-05"
                v-model="twoFactorAuth.mobileNumber"
                placeholder="Enter your mobile number..."
              />
            </el-form-item>
          </el-col>
          <el-col :span="24" :md="12" v-if="twoFactorAuth.method === 'email'">
            <el-input-title required>Email Address</el-input-title>
            <el-form-item prop="email" :rules="twoFactorAuthRules.email">
              <el-input
                class="mt-05"
                v-model="twoFactorAuth.email"
                placeholder="Enter your email address..."
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider class="mt-20"></el-divider>
        <el-row class="d-flex mt-10" :gutter="20">
          <el-col :span="12" class="d-flex justify-content-end">
            <el-button class="bottomBtn" @click="$router.go(-1)"
              >Back</el-button
            >
          </el-col>
          <el-col :span="12">
            <el-button
              type="primary"
              class="btn-highlight bottomBtn"
              @click.prevent="updateSettings"
              >Save</el-button
            >
          </el-col>
        </el-row>
      </el-form>
    </content-body-card>
  </div>
</template>

<script>
import _ from 'lodash';
import { errormsg } from '@/utils/constants';

export default {
  name: 'Settings',
  props: {
  },
  data() {
    const defaultRequiredRule = {
      required: true,
      trigger: 'blur',
      message: errormsg.password,
      validator: (r, value, c) => {
        let valid = true;
        if (!value) valid = false;
        // min length 8
        if (value && value.length < 8) valid = false;
        // any upper case (check only letters)
        if (!_.some(value, (t) => /[A-Z]/.test(t))) valid = false;
        // any lower case (check only letters)
        if (!_.some(value, (t) => /[a-z]/.test(t))) valid = false;
        // any special case
        if (!new RegExp('([`~\\!@#\\$%\\^\\&\\*\\(\\)\\-_\\=\\+\\[\\{\\}\\]\\\\|;:\\\'",<.>\\/\\?â‚¬Â£Â¥â‚¹Â§Â±].*)').test(value)) valid = false;
        // not any space
        if (_.some(value, (t) => t === ' ')) valid = false;

        if (valid) return c();

        return c(new Error(errormsg.password));
      }
    };
    return {
      subTitle: 'Update your user information',
      password: {
        current: undefined,
        update: undefined,
        confirm: undefined
      },
      passwordRules: {
        current: defaultRequiredRule,
        update: defaultRequiredRule,
        confirm: defaultRequiredRule
      },
      profile: {
        gender: ''
      },
      profileRules: {
        gender: {
          required: true,
          trigger: 'blur',
          message: 'Please select a gender'
        }
      },
      genderOptions: [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' }
      ],
      twoFactorAuth: {
        enabled: false,
        method: 'mobile',
        mobileNumber: '',
        email: ''
      },
      passwordRequirements: {
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasSpecialChar: false,
        noSpaces: true
      },
      twoFactorAuthRules: {
        mobileNumber: {
          required: true,
          trigger: 'blur',
          validator: (rule, value, callback) => {
            if (!value && this.twoFactorAuth.enabled && this.twoFactorAuth.method === 'mobile') {
              callback(new Error('Please enter your mobile number'));
            } else {
              callback();
            }
          }
        },
        email: {
          required: true,
          trigger: 'blur',
          type: 'email',
          validator: (rule, value, callback) => {
            if (!value && this.twoFactorAuth.enabled && this.twoFactorAuth.method === 'email') {
              callback(new Error('Please enter a valid email address'));
            } else {
              callback();
            }
          }
        }
      }
    };
  },
  created() {
    this.loadUserSettings();
  },
  methods: {
    loadUserSettings() {
      // Get user's current profile and 2FA settings
      this.$http.get('assemble/api/v1/account/portal/users/profile')
        .then((response) => {
          const userData = response.data.data;

          // Load gender information
          if (userData && userData.profile) {
            this.profile.gender = userData.profile.gender;
          }

          // Load 2FA settings
          if (userData && userData.twoFactorAuth) {
            this.twoFactorAuth.enabled = userData.twoFactorAuth.enabled || false;
            this.twoFactorAuth.method = userData.twoFactorAuth.method || 'mobile';
            if (userData.twoFactorAuth.method === 'mobile') {
              this.twoFactorAuth.mobileNumber = userData.twoFactorAuth.value || '';
            } else if (userData.twoFactorAuth.method === 'email') {
              this.twoFactorAuth.email = userData.twoFactorAuth.value || '';
            }
          }
        })
        .catch((error) => {
          console.error('Failed to load user settings:', error);
        });
    },
    updateSettings() {
      // Check if any password fields are filled
      const isPasswordFilled = this.password.current || this.password.update || this.password.confirm;

      // Always validate the profile form
      this.$refs.profileForm.validate()
        .then((profileValid) => {
          if (!profileValid) {
            this.$message.error('Please complete the required profile fields');
            return;
          }

          // If no password fields are filled, update profile and 2FA settings only
          if (!isPasswordFilled) {
            this.$store.commit('loading', { loading: true, text: 'Loading...' });

            // Update profile first, then 2FA settings
            Promise.all([
              this.saveProfileSettings(),
              this.save2FASettings()
            ])
              .then(([profileSuccess, twoFASuccess]) => {
                if (profileSuccess && twoFASuccess) {
                  this.$message.success('Your settings were updated successfully');
                  this.$store.commit('loading', false);
                  const pathName = this.hasCustomEntity ? 'custom-dashboard' : 'dashboard';
                  this.$router.push({ name: pathName });
                }
              })
              .catch((e) => {
                this.$message.error(e.response?.data?.message || 'An error occurred');
                this.$store.commit('loading', false);
              });
            return;
          }

          // If password fields are filled, validate password form first
          this.$refs.passwordForm.validate()
            .then((passwordValid) => {
              if (!passwordValid) return;

              if (this.password.update !== this.password.confirm) {
                this.$message.error('The update password and confirmation password do not match');
                return;
              }

              this.$store.commit('loading', { loading: true, text: 'Loading...' });

              // Update password, profile, and 2FA settings
              this.$http.put('assemble/api/v1/account/portal/users/password', this.password)
                .then((res) => {
                  this.$store.commit('user/login', res.data.data);

                  // Then update profile and 2FA settings
                  return Promise.all([
                    this.saveProfileSettings(),
                    this.save2FASettings()
                  ]);
                })
                .then(([profileSuccess, twoFASuccess]) => {
                  if (profileSuccess && twoFASuccess) {
                    this.$message.success('Your settings were updated successfully');
                    this.$store.commit('loading', false);
                    const pathName = this.hasCustomEntity ? 'custom-dashboard' : 'dashboard';
                    this.$router.push({ name: pathName });
                  }
                })
                .catch((e) => {
                  this.$message.error(e.response?.data?.message || 'An error occurred');
                  this.$store.commit('loading', false);
                });
            });
        });
    },
    handle2FAToggle(value) {
      if (!value) {
        this.twoFactorAuth.mobileNumber = '';
        this.twoFactorAuth.email = '';
      }
    },
    updatePasswordRequirements() {
      const value = this.password.update;
      if (!value) {
        this.passwordRequirements = {
          minLength: false,
          hasUpperCase: false,
          hasLowerCase: false,
          hasSpecialChar: false,
          noSpaces: true
        };
        return;
      }

      this.passwordRequirements = {
        minLength: value.length >= 8,
        hasUpperCase: /[A-Z]/.test(value),
        hasLowerCase: /[a-z]/.test(value),
        hasSpecialChar: new RegExp('([`~\\!@#\\$%\\^\\&\\*\\(\\)\\-_\\=\\+\\[\\{\\}\\]\\\\|;:\\\'",<.>\\/\\?€£¥₹§±].*)').test(value),
        noSpaces: !value.includes(' ')
      };
    },
    saveProfileSettings() {
      return this.$http.put('assemble/api/v1/account/portal/users/profile', {
        gender: this.profile.gender
      })
        .then(() => {
          // Update the user store with the new gender using the existing complete mutation
          this.$store.commit('user/complete', {
            dob: this.$store.state.user.profile.dob,
            mobile: this.$store.state.user.profile.mobile,
            gender: this.profile.gender
          });
          return true;
        })
        .catch((e) => {
          this.$message.error(e.response?.data?.message || 'Failed to update profile settings');
          return false;
        });
    },
    save2FASettings() {
      if (!this.twoFactorAuth.enabled) {
        // If 2FA is disabled, just send that information
        return this.$http.put('assemble/api/v1/account/portal/users/2fa', {
          enabled: false
        })
          .then(() => true)
          .catch((e) => {
            this.$message.error(e.response?.data?.message || 'Failed to update 2FA settings');
            return false;
          });
      }

      // Validate the 2FA form if enabled
      return this.$refs.twoFactorForm.validate()
        .then((valid) => {
          if (!valid) {
            return Promise.reject(new Error('Please complete the required 2FA fields'));
          }

          // Validate based on the selected method
          if (this.twoFactorAuth.method === 'mobile' && !this.twoFactorAuth.mobileNumber) {
            this.$message.error('Please enter your mobile number');
            return Promise.reject(new Error('Mobile number is required'));
          }

          if (this.twoFactorAuth.method === 'email' && !this.twoFactorAuth.email) {
            this.$message.error('Please enter your email address');
            return Promise.reject(new Error('Email is required'));
          }

          // Send the 2FA settings
          return this.$http.put('assemble/api/v1/account/portal/users/2fa', {
            enabled: this.twoFactorAuth.enabled,
            method: this.twoFactorAuth.method,
            value: this.twoFactorAuth.method === 'mobile' ? this.twoFactorAuth.mobileNumber : this.twoFactorAuth.email
          });
        })
        .then(() => true)
        .catch((e) => {
          if (e.response) {
            this.$message.error(e.response.data.message || 'Failed to update 2FA settings');
          }
          return false;
        });
    }
  },
  computed: {
    hasCustomEntity() {
      const customEntity = this.$store.getters['national/getEntity'];
      if (customEntity && customEntity.level && customEntity.name) {
        return true;
      }
      return false;
    }
  }
};
</script>

<style scoped lang="scss">
.main-content {
  width: 100%;
}

.password-requirements {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
}

.requirement-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #909399;

  &:last-child {
    margin-bottom: 0;
  }

  i {
    margin-right: 8px;
    font-size: 16px;
    color: #f56c6c;
  }

  &.requirement-met {
    color: #606266;

    i {
      color: #67c23a;
    }
  }
}
</style>
